import React, { useCallback, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import CloseButton from 'react-bootstrap/CloseButton';
import Overlay from 'react-bootstrap/Overlay';
import Tooltip from 'react-bootstrap/Tooltip';

import EditAdminUrl from '@src/components/common/Display/EditAdminUrl';
import { ShowStates } from '@src/components/promoCards/partials';
import { getMonthDayYearWithNoLeadingZeros } from '@src/services';
import { collectStates } from '@src/utils/getStatesFromProducts';


import './ResourceTooltip.scss';

const ResourceTooltip = ({
    publishedAt,
    updatedAt,
    effectiveFrom,
    isUpdated,
    products,
    states,
    placement = 'right',
    agents,
    resourceType,
    resourceId,
}) => {
    const [show, setShow] = useState(false);
    const target = useRef(null);
    const timerRef = useRef(null);
    publishedAt = publishedAt ? getMonthDayYearWithNoLeadingZeros(publishedAt) : 'Unpublished';
    updatedAt = updatedAt ? getMonthDayYearWithNoLeadingZeros(updatedAt) : null;
    effectiveFrom = effectiveFrom ? getMonthDayYearWithNoLeadingZeros(effectiveFrom) : null;
    states = collectStates(states, products);

    const handleOnMouseOver = useCallback(() => {
        setShow(true);
    }, [setShow]);

    const handleOnMouseOut = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }

        timerRef.current = setTimeout(() => setShow(false), 3000);
    }, [setShow]);

    const handleOnFocus = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }

        setShow(true);
    }, [setShow]);

    const handleOnBlur = useCallback(() => {
        setShow(false);
    }, [setShow]);

    const handleOnClose = useCallback((e) => {
        e.preventDefault();
        setShow(false);
    }, [setShow]);

    const handleEsc = useCallback((e) => {
        if (e.key === 'Escape') {
            setShow(false);
        }
    }, [setShow]);

    const handleToggleShow = useCallback((e) => {
        e.preventDefault();
        setShow((show) => !show);
    }, []);

    const tooltipInfomation = () => {
        const publishedInfo = isUpdated ? `Updated: ${updatedAt}.` : `Published: ${publishedAt}.`;
        const effectiveInfo = effectiveFrom ? `Effective: ${effectiveFrom}.` : '';

        if (!states || states.length === 0) {
            return `${publishedInfo} ${effectiveInfo}`;
        }

        let statesInfo = '';
        if (states.length >= 51) {
            statesInfo = 'All States';
        } else {
            statesInfo = states.map((state) => state.abbreviation).join(', ');
        }

        return `States: ${statesInfo}. ${publishedInfo} ${effectiveInfo}`;
    };

    return (
        <>
            <a
                ref={target}
                className="tooltip-icon"
                tabIndex={0}
                aria-label="More Information"
                title="More Information"
                style={{ backgroundImage: 'url(/img/icons/tooltip.svg)' }}
                onMouseOver={handleOnMouseOver}
                onMouseOut={handleOnMouseOut}
                onFocus={handleOnFocus}
                onBlur={handleOnBlur}
                onKeyDown={handleEsc}
                onClick={handleToggleShow}
            >
                { tooltipInfomation() }
            </a>
            <Overlay target={target.current} show={show} placement={placement}>
                {(props) => (
                    <Tooltip {...props} className="resource-tooltip">
                        <CloseButton aria-label="Hide" onClick={handleOnClose} />
                        <div className="tool-tip-item-container">
                            <ShowStates states={states} />
                        </div>
                        <div className="tool-tip-item-container">
                            {
                                agents && <span className="tooltip-header">Agents: {agents}</span>
                            }
                            {
                                !isUpdated
                                    && (
                                        <>
                                            <span className="tooltip-header tooltip-published">Published:</span>
                                            <span className="tooltip-values tooltip-published">{publishedAt}</span>
                                        </>
                                    )
                            }
                            {
                                isUpdated && updatedAt
                                    && (
                                        <>
                                            <span className="tooltip-header tooltip-published">Updated:</span>
                                            <span className="tooltip-values tooltip-published">{updatedAt}</span>
                                        </>
                                    )
                            }
                        </div>
                        <div className="tool-tip-item-container">
                            {
                                effectiveFrom
                                    && (
                                        <>
                                            <span className="tooltip-header tooltip-published">Effective:</span>
                                            <span className="tooltip-values tooltip-published">{effectiveFrom}</span>
                                        </>
                                    )
                            }
                            {
                                resourceType && resourceId
                                    && (
                                        <EditAdminUrl resourceType={resourceType} id={resourceId} />
                                    )
                            }
                        </div>
                    </Tooltip>
                )}
            </Overlay>
        </>
    );
};

ResourceTooltip.propTypes = {
    placement: PropTypes.string,
    publishedAt: PropTypes.string,
    updatedAt: PropTypes.string,
    isUpdated: PropTypes.bool,
    effectiveFrom: PropTypes.string,
    products: PropTypes.arrayOf(PropTypes.object),
    states: PropTypes.arrayOf(PropTypes.object),
    agents: PropTypes.string,
    resourceType: PropTypes.string,
    resourceId: PropTypes.number,
};

export default React.memo(ResourceTooltip);
